'use client';

import { useEffect, useRef, useState } from 'react';
import { TopicCardClient } from './TopicCardClient';
import { TopicListItem } from '@/libs/topics';

type Props = {
  apiUrl: string;
  startPage: number;
  hasMoreInitial: boolean;
};

export const InfiniteTopics = ({ apiUrl, startPage, hasMoreInitial }: Props) => {
  const [page, setPage] = useState(startPage);
  const [items, setItems] = useState<TopicListItem[]>([]);
  const [hasMore, setHasMore] = useState(hasMoreInitial);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const storageKey = `infinite:${apiUrl}`;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw) as {
          items: TopicListItem[];
          page: number;
          hasMore: boolean;
          scrollY?: number;
        };
        setItems(saved.items || []);
        setPage(saved.page || startPage);
        setHasMore(typeof saved.hasMore === 'boolean' ? saved.hasMore : hasMoreInitial);

        const y = saved.scrollY ?? 0;
        if (y > 0) {
          requestAnimationFrame(() => {
            window.scrollTo(0, y);
          });
        }
      }
    } catch {}
  }, [hasMoreInitial, startPage, storageKey]);

  useEffect(() => {
    try {
      const payload = JSON.stringify({ items, page, hasMore, scrollY: window.scrollY });
      sessionStorage.setItem(storageKey, payload);
    } catch {}
  }, [items, page, hasMore, storageKey]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          try {
            const raw = sessionStorage.getItem(storageKey);
            const data = raw ? JSON.parse(raw) : {};
            data.scrollY = window.scrollY;
            sessionStorage.setItem(storageKey, JSON.stringify(data));
          } catch {}
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [storageKey]);

  useEffect(() => {
    if (!hasMore) {
      return;
    }

    const el = sentinelRef.current;
    if (!el) {
      return;
    }

    const onIntersect: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !loading && hasMore) {
          void loadMore();
        }
      });
    };

    const observer = new IntersectionObserver(onIntersect, { rootMargin: '200px' });
    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading]);

  const loadMore = async () => {
    if (loading || !hasMore) {
      return;
    }

    setLoading(true);

    try {
      const sep = apiUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${apiUrl}${sep}page=${page}`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error('Failed to load');
      }

      const json = await response.json();
      const newItems: TopicListItem[] = json.data || [];
      setItems((prev) => [...prev, ...newItems]);
      setHasMore(Boolean(json.hasMore));
      setPage((currentPage) => currentPage + 1);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {items.map((topic) => (
        <TopicCardClient key={topic.topicId} topic={topic} />
      ))}
      {hasMore ? (
        <div ref={sentinelRef} className="w-full flex items-center justify-center py-8 text-default-500">
          {loading ? 'Завантаження…' : 'Гортайте вниз для завантаження ще'}
        </div>
      ) : null}
    </>
  );
};
